import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useStable } from "../context/StableContext";
import { InfoHint } from "../components/InfoHint";
import { useAuth } from "../context/AuthContext";
import api, { errText, fileUrl } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import StatusDot from "../components/StatusDot";
import { sortOverdue, buildCatMap } from "../lib/deadlineSort";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateField } from "../components/DateField";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Plus, CheckCircle2, Pencil, Trash2, Loader2, Paperclip, Upload, X, Search, AlertTriangle, RefreshCw, BellOff, BellRing, Radar } from "lucide-react";
import { HorsePicker } from "../components/HorsePicker";
import { SearchSelect } from "../components/SearchSelect";
import { ViewTabs } from "../components/ViewTabs";
import { ContextBanner } from "../components/ContextBanner";
import { timeToDue } from "../lib/timeToDue";
import { matchHorse, matchDeadline } from "../lib/horseSearch";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "—");
const RMONTHS = { "3m": 3, "6m": 6, "12m": 12, "2y": 24, "3y": 36 };
const addMonthsISO = (iso, m) => { const d = new Date(iso + "T00:00:00"); const day = d.getDate(); d.setMonth(d.getMonth() + m); if (d.getDate() < day) d.setDate(0); return d.toISOString().slice(0, 10); };
const DPERIODS = [["30", "30 giorni"], ["60", "60 giorni"], ["90", "90 giorni"], ["180", "6 mesi"], ["365", "1 anno"], ["730", "2 anni"], ["1095", "3 anni"], ["all", "Tutto"]];
const daysUntilD = (iso) => { const d = new Date(iso.slice(0, 10) + "T00:00:00"); const t = new Date(); t.setHours(0, 0, 0, 0); return (d - t) / 86400000; };
const RECURRENCE = [
  { v: "none", l: "Nessuna" }, { v: "custom", l: "Data personalizzata" }, { v: "3m", l: "Ogni 3 mesi" },
  { v: "6m", l: "Ogni 6 mesi" }, { v: "12m", l: "Ogni 12 mesi" }, { v: "2y", l: "Ogni 2 anni" }, { v: "3y", l: "Ogni 3 anni" },
];
const EMPTY = { horse_id: "", horse_ids: [], category: "", description: "", due_date: "", last_completion_date: "", recurrence: "none", reminder_days: 7, managed_by: "scuderia", is_missing: false, notes: "", due_auto: true };
const STATE_META = {
  COMPLETE_EXISTING: { cls: "border-[#CDE3D4] bg-[#F1F8F3]", icon: "check" },
  REGULARIZE_MISSING: { cls: "border-[#F2D9E2] bg-[#FBF3F6]", icon: "check" },
  SUPERSEDE_FUTURE: { cls: "border-[#F0DDBB] bg-[#FBF3E4]", icon: "warn" },
  CREATE_NEW: { cls: "border-[#CDE3D4] bg-[#F5FBF6]", icon: "plus" },
  CONFLICT: { cls: "border-[#F0C7C4] bg-[#FCF1F0]", icon: "alert" },
};
// Coverage states (population-first diagnostic). "no_record" is DISTINCT from "mancante".
const COV_STATES = [
  { v: "all", l: "Tutti" },
  { v: "valid", l: "Valido / presente" },
  { v: "in_scadenza", l: "In scadenza" },
  { v: "scaduto", l: "Scaduto" },
  { v: "mancante", l: "Da regolarizzare" },
  { v: "non_monitorato", l: "Non monitorato" },
  { v: "no_record", l: "Nessun record" },
  { v: "proprietario", l: "Gestita dal proprietario" },
];
const COV_META = {
  valid: { l: "Valido", cls: "text-[#1F7A45]", dot: "#1F7A45" },
  in_scadenza: { l: "In scadenza", cls: "text-[#9A6516]", dot: "#C9A227" },
  scaduto: { l: "Scaduto", cls: "text-[#DC2626]", dot: "#DC2626" },
  mancante: { l: "Da regolarizzare", cls: "text-[#A03D62]", dot: "#A03D62" },
  non_monitorato: { l: "Non monitorato", cls: "text-[#8C6D4F]", dot: "#C9BDA0" },
  no_record: { l: "Nessun record", cls: "text-[#5F6B64]", dot: "#C9BDA0" },
  proprietario: { l: "Gestita dal proprietario", cls: "text-[#8C532B]", dot: "#8C532B" },
};

export default function Deadlines() {
  const { current, perms } = useStable();
  const { user, updatePreferences } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [horses, setHorses] = useState([]);
  const [cats, setCats] = useState([]);
  const [horizon, setHorizon] = useState(user?.preferences?.deadline_horizon || "90");
  const [hq, setHq] = useState("");
  const [dialog, setDialog] = useState(null); // form obj or null
  const [complete, setComplete] = useState(null); // deadline being completed
  const [compDate, setCompDate] = useState(new Date().toISOString().slice(0, 10));
  const [compNext, setCompNext] = useState("");
  const [compRec, setCompRec] = useState("none");
  const [compDesc, setCompDesc] = useState("");
  const [compNotes, setCompNotes] = useState("");
  const [compFiles, setCompFiles] = useState([]);
  const [compUploading, setCompUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [undo, setUndo] = useState(null);
  const [view, setView] = useState("active"); // active | history
  const [history, setHistory] = useState(null);
  const [histHorse, setHistHorse] = useState("all");
  const [histCat, setHistCat] = useState("all");
  const [delHist, setDelHist] = useState(null);
  const [histAction, setHistAction] = useState(null);
  const [histEdit, setHistEdit] = useState(null);
  const [histEditConfirm, setHistEditConfirm] = useState(false);
  const [histSaving, setHistSaving] = useState(false);
  const [suspendItem, setSuspendItem] = useState(null);
  const [suspendNote, setSuspendNote] = useState("");
  // Ricerca copertura (population-first)
  const [covOpen, setCovOpen] = useState(false);
  const [covCat, setCovCat] = useState("");
  const [covPresence, setCovPresence] = useState("presente");
  const [covStatus, setCovStatus] = useState("all");
  const [covLoading, setCovLoading] = useState(false);
  const [covData, setCovData] = useState(null);
  const [covSelected, setCovSelected] = useState([]);
  const [covAction, setCovAction] = useState(null);
  const [covActing, setCovActing] = useState(false);
  const horseFilter = params.get("horse");
  const seeAll = params.get("all") === "1";
  const focusId = params.get("focus");
  const focusDate = params.get("date");
  const groupParam = params.get("group"); // overdue | upcoming | missing (Dashboard deep-link subset)
  const changeHorizon = (v) => { setHorizon(v); updatePreferences({ deadline_horizon: v }); const p = new URLSearchParams(params); p.delete("all"); p.delete("focus"); p.delete("group"); setParams(p, { replace: true }); };
  const clearContext = () => { const p = new URLSearchParams(params); p.delete("all"); p.delete("focus"); p.delete("date"); p.delete("group"); setParams(p, { replace: true }); };

  const load = useCallback(() => {
    if (!current) return;
    setItems(null);
    const q = horseFilter ? { horse_id: horseFilter } : {};
    api.get(`/stables/${current.id}/deadlines`, { params: q }).then((r) => setItems(r.data));
  }, [current, horseFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!current) return;
    api.get(`/stables/${current.id}/horses`).then((r) => setHorses(r.data));
    api.get(`/stables/${current.id}/deadline-categories`).then((r) => setCats(r.data));
  }, [current]);

  useEffect(() => {
    if (current && params.get("new") === "1" && perms.canOps) {
      setDialog({ ...EMPTY, horse_ids: horseFilter ? [horseFilter] : [], due_auto: false });
      const p = new URLSearchParams(params); p.delete("new"); setParams(p, { replace: true });
    }
  }, [current, params]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = useCallback(() => {
    if (!current) return;
    setHistory(null);
    const q = {};
    if (histHorse !== "all") q.horse_id = histHorse;
    if (histCat !== "all") q.category = histCat;
    api.get(`/stables/${current.id}/health-history`, { params: q }).then((r) => setHistory(r.data));
  }, [current, histHorse, histCat]);
  useEffect(() => { if (view === "history") loadHistory(); }, [view, loadHistory]);
  useEffect(() => { if (seeAll || focusId || focusDate || groupParam) setView("active"); }, [seeAll, focusId, focusDate, groupParam]);
  useEffect(() => { const p = user?.preferences?.deadline_horizon; if (p) setHorizon(p); }, [user?.preferences?.deadline_horizon]);

  const undoText = (h) => {
    const res = h?.resolution;
    if (res === "regularized") return "Il record tornerà come «Mancante / da regolarizzare».";
    if (res === "created") return "Questa registrazione verrà rimossa. Prima non esisteva alcuna scadenza precedente.";
    if (res === "superseded") return `Verrà ripristinata la precedente scadenza del ${fmt(h.previous_due_date)}.`;
    return `Verrà rimossa la nuova scadenza${h?.next_due_date ? ` del ${fmt(h.next_due_date)}` : ""} e ripristinata la precedente${h?.previous_due_date ? ` del ${fmt(h.previous_due_date)}` : ""}.`;
  };
  const doUndo = async () => {
    setSaving(true);
    try {
      await api.post(`/stables/${current.id}/health-history/${undo.id}/undo`);
      toast.success("Registrazione annullata");
      setUndo(null); loadHistory(); load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // Ricerca copertura (population-first): read-only diagnostic, never creates records.
  const runCoverage = async () => {
    if (!(covCat || "").trim()) return toast.error("Seleziona una prestazione");
    setCovLoading(true); setCovSelected([]); setCovAction(null);
    try {
      const r = await api.post(`/stables/${current.id}/deadline-categories/coverage`,
        { category: covCat, presence: covPresence, status_filter: covStatus });
      setCovData(r.data);
    } catch (err) { toast.error(errText(err.response?.data?.detail)); setCovData(null); }
    finally { setCovLoading(false); }
  };
  const toggleCovSel = (id) => setCovSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const selectAllCov = () => setCovSelected((covData?.results || []).map((r) => r.horse_id));
  const deselectCov = () => setCovSelected([]);
  const runRespAction = async (managed_by) => {
    if (!covSelected.length) return;
    setCovActing(true);
    try {
      const r = await api.post(`/stables/${current.id}/deadline-responsibilities/bulk`,
        { horse_ids: covSelected, category: covCat, managed_by, dry_run: true });
      setCovAction({ managed_by, preview: r.data });
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setCovActing(false); }
  };
  const confirmRespAction = async () => {
    if (!covAction) return;
    setCovActing(true);
    try {
      await api.post(`/stables/${current.id}/deadline-responsibilities/bulk`,
        { horse_ids: covSelected, category: covCat, managed_by: covAction.managed_by, dry_run: false });
      toast.success(covAction.managed_by === "proprietario" ? "Impostata gestione proprietario" : "Impostata gestione scuderia");
      setCovAction(null); setCovSelected([]); await runCoverage(); load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setCovActing(false); }
  };
  const createFromCoverage = () => {
    if (!covSelected.length) return;
    setDialog({ ...EMPTY, horse_ids: covSelected, category: covCat, due_auto: false, _coverage: true });
    setCovOpen(false);
  };

  const doComplete = async () => {
    setSaving(true);
    try {
      await api.post(`/stables/${current.id}/deadlines/${complete.id}/complete`,
        { completion_date: compDate, recurrence: compRec, next_due_date: compRec === "custom" ? (compNext || null) : null, description: compDesc || null, notes: compNotes || null, attachments: compFiles });
      toast.success("Completamento registrato nello storico sanitario");
      setComplete(null); setCompNext(""); setCompFiles([]); load();
      if (view === "history") loadHistory();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadCompFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setCompUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setCompFiles((prev) => [...prev, { path: data.path, name: f.name }]);
      }
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setCompUploading(false); e.target.value = ""; }
  };

  const remove = async (d) => {
    if (!window.confirm("Eliminare questa scadenza?")) return;
    await api.delete(`/stables/${current.id}/deadlines/${d.id}`);
    toast.success("Scadenza eliminata"); load();
  };

  const doSuspend = async () => {
    if (!suspendItem) return;
    setSaving(true);
    try {
      await api.post(`/stables/${current.id}/deadlines/${suspendItem.id}/suspend-monitoring`, { note: suspendNote || null });
      toast.success("Monitoraggio sospeso"); setSuspendItem(null); setSuspendNote(""); load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const doResume = async (d) => {
    try {
      await api.post(`/stables/${current.id}/deadlines/${d.id}/resume-monitoring`);
      toast.success("Monitoraggio riattivato"); load();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  const deleteHistory = async () => {
    try {
      await api.delete(`/stables/${current.id}/health-history/${delHist.id}`);
      toast.success("Registrazione eliminata definitivamente");
      setDelHist(null); loadHistory();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
  };

  // Correzione di un record STORICO sanitario (solo dati documentali; non tocca la scadenza attiva né le ricorrenze).
  const openHistEdit = (h) => {
    if (!h) return;
    setHistEdit({ id: h.id, horse_name: h.horse_name, category: h.category,
      completion_date: h.completion_date, description: h.description || "", notes: h.notes || "" });
    setHistAction(null);
  };

  const saveHistEdit = async () => {
    if (!histEdit) return;
    setHistSaving(true);
    const payload = { completion_date: histEdit.completion_date, description: histEdit.description || null, notes: histEdit.notes || null };
    try {
      await api.put(`/stables/${current.id}/health-history/${histEdit.id}`, payload);
      toast.success("Record storico aggiornato");
      setHistEditConfirm(false); setHistEdit(null); loadHistory();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setHistSaving(false); }
  };

  const catMap = buildCatMap(cats);
  const activeCats = cats.filter((c) => !c.archived);
  const focusItem = focusId && items ? items.find((d) => d.id === focusId) : null;

  const isMonitored = (d) => d.monitoring_active !== false;
  const effHorizon = (seeAll || focusDate) ? "all" : horizon;
  let baseItems = (items || []).filter((d) => matchDeadline(d, hq));
  if (focusDate) baseItems = baseItems.filter((d) => String(d.due_date).slice(0, 10) === focusDate);
  const monItems = baseItems.filter(isMonitored);
  const unmonitored = baseItems.filter((d) => !isMonitored(d));
  const dMissing = focusDate ? [] : monItems.filter((d) => d.status === "mancante");
  const dScadute = sortOverdue(monItems.filter((d) => d.status === "scaduto"), catMap);
  let periodFuture = monItems.filter((d) => !d.completed && d.status !== "scaduto" && d.status !== "mancante"
    && (effHorizon === "all" || daysUntilD(d.due_date) <= Number(effHorizon)));
  if (focusItem && focusItem.status !== "scaduto" && focusItem.status !== "mancante" && isMonitored(focusItem) && !periodFuture.some((d) => d.id === focusItem.id)) periodFuture = [focusItem, ...periodFuture];
  periodFuture.sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));
  // Dashboard deep-link subset (group=overdue|upcoming|missing). "upcoming" uses the same 45-day radar as the Dashboard.
  const upcoming45 = monItems.filter((d) => !d.completed && d.status !== "scaduto" && d.status !== "mancante" && daysUntilD(d.due_date) <= 45)
    .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));
  const showScaduteG = !groupParam || groupParam === "overdue";
  const showUpcomingG = !groupParam || groupParam === "upcoming";
  const showMissingG = !groupParam || groupParam === "missing";
  const showUnmon = !groupParam && !focusDate;
  const upcomingList = groupParam === "upcoming" ? upcoming45 : periodFuture;
  const GROUP_LABEL = { overdue: "Scaduti", upcoming: "In scadenza", missing: "Da regolarizzare" };
  const groupCount = groupParam === "overdue" ? dScadute.length : groupParam === "upcoming" ? upcoming45.length : groupParam === "missing" ? dMissing.length : 0;
  const ctxLabel = groupParam ? `${GROUP_LABEL[groupParam]} · ${groupCount}`
    : focusDate ? `Scadenze del ${fmt(focusDate)}` : (seeAll ? "tutte le scadenze" : (focusItem ? `${focusItem.horse_name} · ${focusItem.category} · ${fmt(focusItem.due_date)}` : (focusId ? "scadenza selezionata" : "")));

  const renderUnmonitored = (d) => {
    const missing = d.status === "mancante" || !d.due_date;
    const tt = missing ? null : timeToDue(d.due_date);
    return (
      <div key={d.id} data-testid={`deadline-unmonitored-${d.id}`} className="flex items-center gap-3 px-4 py-3">
        <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0 bg-[#C9BDA0]" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#41504A] truncate">{d.horse_name}</p>
          <p className="text-xs text-[#8C6D4F] truncate">{d.category}{d.description ? ` · ${d.description}` : ""}</p>
          <p className="text-xs font-medium mt-0.5 flex flex-wrap items-center gap-x-1.5">
            {missing ? <span className="text-[#A03D62]">Mancante</span> : <span style={{ color: tt.color }}>{tt.label} · {fmt(d.due_date)}</span>}
            <span className="inline-flex items-center gap-1 text-[#8C6D4F]"><BellOff className="h-3 w-3" /> Non monitorato</span>
          </p>
          {d.monitoring_note ? <p className="text-[11px] text-[#8C6D4F] mt-0.5 italic truncate">{d.monitoring_note}</p> : null}
        </div>
        {perms.canManage && (
          <Button variant="ghost" size="sm" data-testid={`resume-monitoring-${d.id}`} onClick={() => doResume(d)} className="text-[#1F7A45] hover:text-[#155e35] h-8 shrink-0">
            <BellRing className="h-3.5 w-3.5 mr-1" /> Riattiva
          </Button>
        )}
      </div>
    );
  };

  const openComplete = (d) => { setComplete(d); setCompDate(new Date().toISOString().slice(0, 10)); setCompNext(""); setCompRec(d.recurrence || "none"); setCompDesc(d.description || ""); setCompNotes(d.notes || ""); setCompFiles([]); };

  const renderDeadline = (d) => {
    const isMissing = d.status === "mancante";
    const tt = isMissing ? null : timeToDue(d.due_date);
    const content = (
      <>
        <p className="font-semibold text-[#1B3B2B] truncate">{d.horse_name}</p>
        <p className="text-xs text-[#41504A] truncate">{d.category}{d.description ? ` · ${d.description}` : ""}{d.managed_by === "proprietario" ? <span className="text-[#8C532B]"> · Proprietario</span> : null}</p>
        {isMissing ? (
          <p className="text-xs font-medium mt-0.5">
            <span data-testid={`deadline-missing-badge-${d.id}`} className="inline-flex items-center rounded-full bg-[#FBF3F6] text-[#A03D62] px-2 py-0.5 text-[11px] font-semibold">Mancante</span>
          </p>
        ) : (
          <p className="text-xs font-medium mt-0.5 flex flex-wrap gap-x-1.5">
            <span className="text-[#5F6B64]">{fmt(d.due_date)}</span>
            <span style={{ color: tt.color }}>· {tt.label}</span>
          </p>
        )}
      </>
    );
    return (
      <div key={d.id} data-testid={`deadline-row-${d.id}`} className={`flex items-center gap-3 px-4 py-3 ${focusId === d.id ? "ring-2 ring-[#8C532B] ring-inset bg-[#FFFBEB]" : ""}`}>
        <StatusDot status={isMissing ? "futuro" : tt.dot} />
        {perms.canOps ? (
          <button type="button" data-testid={`complete-from-row-${d.id}`} onClick={() => openComplete(d)}
            className="flex-1 min-w-0 text-left -my-3 py-3 rounded-lg hover:bg-[#FAF8F5] transition-colors">
            {content}
          </button>
        ) : (
          <div className="flex-1 min-w-0">{content}</div>
        )}
        {perms.canOps && (
          <div className="flex gap-1 shrink-0">
            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDialog({ ...EMPTY, ...d, reminder_days: d.reminder_days ?? 7, due_auto: false }); }} data-testid={`edit-deadline-${d.id}`} title="Modifica">
              <Pencil className="h-4 w-4 text-[#5F6B64]" />
            </Button>
            {perms.canManage && (
              <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setSuspendItem(d); setSuspendNote(""); }} data-testid={`suspend-monitoring-${d.id}`} title="Sospendi monitoraggio">
                <BellOff className="h-4 w-4 text-[#8C6D4F]" />
              </Button>
            )}
            {perms.canManage && (
              <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); remove(d); }} data-testid={`delete-deadline-${d.id}`} title="Elimina">
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 eq-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono-label text-[#8C532B] text-xs">Sanitario e documenti</p>
          <h1 className="font-display text-2xl lg:text-3xl font-bold text-[#1B3B2B]">Scadenzario</h1>
        </div>
        {perms.canOps && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="outline" data-testid="coverage-open-btn" onClick={() => { setCovOpen(true); setCovData(null); setCovSelected([]); }}
              className="border-[#1B3B2B] text-[#1B3B2B] hover:bg-[#F1F4F0]">
              <Radar className="h-4 w-4 mr-2" /> Ricerca copertura
            </Button>
            <Button data-testid="add-deadline-btn" onClick={() => setDialog({ ...EMPTY })} className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              <Plus className="h-4 w-4 mr-2" /> Nuova scadenza
            </Button>
          </div>
        )}
      </div>

      <ViewTabs value={view} onChange={setView}
        options={[{ value: "active", label: "Scadenze", testid: "view-active" }, { value: "history", label: "Storico sanitario", testid: "view-history" }]} />

      {view === "active" && ctxLabel && (
        <ContextBanner label={ctxLabel} onClear={clearContext} testid="deadline-context-banner" />
      )}

      {view === "active" ? (
      <>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C532B]" />
          <input data-testid="deadline-search" value={hq} onChange={(e) => setHq(e.target.value)} placeholder="Cerca cavallo o prestazione…"
            className="w-full pl-9 pr-3 py-2 rounded-md bg-white border border-[#E5DEC9] text-sm outline-none placeholder:text-[#9aa39c]" />
        </div>
        <div className="hidden lg:flex flex-wrap gap-1.5">
          {DPERIODS.map(([v, l]) => (
            <button key={v} data-testid={`deadline-period-${v}`} onClick={() => changeHorizon(v)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium border transition-colors ${
                horizon === v ? "bg-[#1B3B2B] text-[#FAF8F5] border-[#1B3B2B]" : "bg-white text-[#5F6B64] border-[#E5DEC9] hover:border-[#8C532B]"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="lg:hidden flex items-center gap-2">
          <span className="text-sm text-[#5F6B64] shrink-0">Periodo:</span>
          <Select value={horizon} onValueChange={changeHorizon}>
            <SelectTrigger className="flex-1 bg-white h-9" data-testid="deadline-period-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DPERIODS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`deadline-period-opt-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!items ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" /></div>
      ) : (() => {
        const showScad = showScaduteG && dScadute.length > 0;
        const showUpc = showUpcomingG && upcomingList.length > 0;
        const showMiss = showMissingG && dMissing.length > 0;
        const showUnm = showUnmon && unmonitored.length > 0;
        if (!showScad && !showUpc && !showMiss && !showUnm) {
          return <div className="bg-white rounded-xl border border-[#E5DEC9] py-16 text-center text-[#5F6B64]">Nessuna scadenza da mostrare.</div>;
        }
        return (
        <div className="space-y-4">
          {showScad && (
            <div data-testid="deadline-overdue-group">
              <h3 className="font-mono-label text-xs uppercase text-[#DC2626] mb-1.5 px-1">Scadute</h3>
              <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1]">{dScadute.map(renderDeadline)}</div>
            </div>
          )}
          {showUpc && (
            <div data-testid="deadline-upcoming-group">
              <h3 className="font-mono-label text-xs uppercase text-[#8C532B] mb-1.5 px-1">In scadenza</h3>
              <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1]">{upcomingList.map(renderDeadline)}</div>
            </div>
          )}
          {showMiss && (
            <div data-testid="deadline-missing-group">
              <h3 className="font-mono-label text-xs uppercase text-[#A03D62] mb-1.5 px-1">Da regolarizzare</h3>
              <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1]">{dMissing.map(renderDeadline)}</div>
            </div>
          )}
          {showUnm && (
            <div data-testid="deadline-unmonitored-group" className="pt-2">
              <h3 className="font-mono-label text-xs uppercase text-[#8C6D4F] mb-1.5 px-1 flex items-center gap-1.5"><BellOff className="h-3 w-3" /> Non monitorati <InfoHint testId="help-non-monitorato" label="Cosa significa Non monitorato" text="La scadenza resta registrata ma non compare negli avvisi operativi finché il monitoraggio è sospeso." /></h3>
              <div className="bg-[#FAF8F3] rounded-xl border border-dashed border-[#DED6C2] divide-y divide-[#EFE8D9]">{unmonitored.map(renderUnmonitored)}</div>
            </div>
          )}
        </div>
        );
      })()}
      </>
      ) : (
        !history ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C532B]" />
                <input data-testid="history-search" value={hq} onChange={(e) => setHq(e.target.value)} placeholder="Cerca cavallo…"
                  className="w-full pl-9 pr-3 py-2 rounded-md bg-white border border-[#E5DEC9] text-sm outline-none placeholder:text-[#9aa39c]" />
              </div>
              <Select value={histHorse} onValueChange={setHistHorse}>
                <SelectTrigger className="w-full sm:w-[200px] bg-white" data-testid="history-horse-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i cavalli</SelectItem>
                  {horses.map((h) => <SelectItem key={h.id} value={h.id}>{h.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={histCat} onValueChange={setHistCat}>
                <SelectTrigger className="w-full sm:w-[220px] bg-white" data-testid="history-category-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(() => { const hist = history.filter((h) => matchDeadline(h, hq)); return hist.length === 0 ? (
              <div className="bg-white rounded-xl border border-[#E5DEC9] py-16 text-center text-[#5F6B64]">Nessuna registrazione sanitaria completata.</div>
            ) : (
              <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1]">
                {hist.map((h) => (
                  <div key={h.id} data-testid={`history-row-${h.id}`} className={`flex items-center gap-4 px-4 py-3 ${h.rolled_back ? "opacity-60" : ""}`}>
                    <button type="button" data-testid={`open-history-record-${h.id}`} onClick={() => setHistAction(h)}
                      className="flex-1 min-w-0 text-left group -my-2 py-2 rounded-lg hover:bg-[#FAF8F5] transition-colors">
                      <p className="font-semibold text-[#1B3B2B] group-hover:text-[#8C532B] transition-colors">{h.horse_name}</p>
                      <p className="text-xs text-[#5F6B64] mt-0.5">
                        {h.category} · Completato {fmt(h.completion_date)}
                        {h.next_due_date ? ` · Prossima scadenza ${fmt(h.next_due_date)}` : ""}
                        {h.description ? ` · ${h.description}` : ""}
                        {h.notes ? ` · ${h.notes}` : ""}
                        {h.edited_at ? " · modificato" : ""}
                      </p>
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                      {h.attachments?.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-[#8C532B]" title={`${h.attachments.length} allegato/i`}>
                          <Paperclip className="h-3 w-3" /> {h.attachments.length}
                        </span>
                      )}
                      {h.rolled_back ? (
                        <span data-testid={`history-undone-${h.id}`} className="text-[11px] font-medium text-[#8C6D4F]">Registrazione annullata</span>
                      ) : (
                        <>
                          {h.can_undo && (
                            <Button variant="ghost" size="sm" data-testid={`undo-registration-${h.id}`} onClick={() => setUndo(h)} className="text-[#8C532B] hover:text-[#6E3F20] h-8">
                              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Annulla
                            </Button>
                          )}
                          <StatusBadge status="completato" />
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ); })()}
          </div>
        )
      )}

      {dialog && <DeadlineFormDialog initial={dialog} stableId={current.id} horses={horses} activeCats={activeCats} onClose={() => setDialog(null)} onSaved={() => { load(); if (view === "history") loadHistory(); }} />}

      {/* Undo registration confirm */}
      <Dialog open={!!undo} onOpenChange={(o) => !o && setUndo(null)}>
        <DialogContent data-testid="undo-confirm">
          <DialogHeader><DialogTitle className="font-display">Annulla registrazione</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5F6B64]">{undo?.horse_name} · {undo?.category}</p>
          <p className="text-sm text-[#2D3832]">{undo ? undoText(undo) : ""}</p>
          <p className="text-xs text-[#8C6D4F]">Verrà ripristinato lo stato immediatamente precedente a questa registrazione.</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setUndo(null)}>Annulla</Button>
            <Button onClick={doUndo} disabled={saving} data-testid="undo-confirm-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Ripristina situazione precedente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete dialog */}
      <Dialog open={!!complete} onOpenChange={(o) => !o && setComplete(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-1.5">Registra completamento<InfoHint testId="help-completa" label="Cosa fa Completa" text="Registra la prestazione come eseguita e aggiorna lo storico. Se prevista, Equiro può generare automaticamente la prossima scadenza." /></DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5F6B64]">{complete?.category} — {complete?.horse_name}</p>
            <div><Label>Data completamento</Label><DateField testId="complete-date" className="mt-1.5" value={compDate} onChange={setCompDate} /></div>
            <div><Label>Descrizione</Label><Input className="mt-1.5" data-testid="complete-description" value={compDesc} onChange={(e) => setCompDesc(e.target.value)} placeholder="Es. Vaccino richiamo eseguito" /></div>
            <div><Label>Note</Label><Textarea className="mt-1.5" data-testid="complete-notes" value={compNotes} onChange={(e) => setCompNotes(e.target.value)} /></div>
            <div>
              <Label>Allegati (PDF, JPG, PNG)</Label>
              <div className="mt-1.5 space-y-2">
                {compFiles.map((f, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] px-3 py-1.5 text-sm">
                    <span className="flex items-center gap-2 min-w-0 text-[#1B3B2B]"><Paperclip className="h-3.5 w-3.5 text-[#8C532B]" /><span className="truncate">{f.name}</span></span>
                    <button type="button" onClick={() => setCompFiles((prev) => prev.filter((_, idx) => idx !== i))} data-testid={`remove-attachment-${i}`} className="text-[#8C532B] hover:text-red-600"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                <label className="inline-flex items-center gap-2 rounded-lg border border-dashed border-[#C9BDA0] px-3 py-2 text-sm text-[#8C532B] cursor-pointer hover:border-[#8C532B]" data-testid="complete-attach-label">
                  {compUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Aggiungi allegato
                  <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*" onChange={uploadCompFiles} data-testid="complete-attach-input" />
                </label>
              </div>
            </div>
            <div className="rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] p-3 space-y-3">
              <p className="font-mono-label text-[#8C532B] text-xs">Prossima scadenza</p>
              <div>
                <Label>Ricorrenza</Label>
                <Select value={compRec} onValueChange={setCompRec}>
                  <SelectTrigger className="mt-1.5" data-testid="complete-recurrence"><SelectValue /></SelectTrigger>
                  <SelectContent>{RECURRENCE.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {compRec === "custom" ? (
                <div><Label>Data prevista</Label><DateField testId="complete-next" className="mt-1.5" value={compNext} onChange={setCompNext} /></div>
              ) : compRec === "none" ? (
                <p className="text-sm text-[#5F6B64]" data-testid="complete-next-preview">Nessuna prossima scadenza verrà creata.</p>
              ) : (
                <p className="text-sm text-[#1B3B2B]" data-testid="complete-next-preview">Data prevista: <span className="font-semibold">{fmt(addMonthsISO(compDate, RMONTHS[compRec]))}</span></p>
              )}
              {compRec === "custom" && !compNext && (
                <p className="text-xs text-red-600" data-testid="complete-custom-warning">Specifica la data prevista.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComplete(null)}>Annulla</Button>
            <Button onClick={doComplete} disabled={saving || compUploading || (compRec === "custom" && !compNext)} data-testid="confirm-complete-btn" className="bg-[#2E7D32] hover:bg-[#256628]">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Azioni sul record storico sanitario (apribile dalla riga dello Storico) */}
      <Dialog open={!!histAction} onOpenChange={(o) => !o && setHistAction(null)}>
        <DialogContent data-testid="history-record-actions">
          <DialogHeader><DialogTitle className="font-display">Record storico — {histAction?.horse_name}</DialogTitle></DialogHeader>
          {histAction && (
            <div className="space-y-1 text-sm">
              <p><span className="text-[#8C532B] font-medium">Categoria:</span> {histAction.category}</p>
              <p><span className="text-[#8C532B] font-medium">Completato:</span> {fmt(histAction.completion_date)}</p>
              {histAction.next_due_date && <p><span className="text-[#8C532B] font-medium">Prossima scadenza:</span> {fmt(histAction.next_due_date)}</p>}
              {histAction.description && <p><span className="text-[#8C532B] font-medium">Descrizione:</span> {histAction.description}</p>}
              {histAction.notes && <p><span className="text-[#8C532B] font-medium">Note:</span> {histAction.notes}</p>}
              {histAction.attachments?.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {histAction.attachments.map((a, i) => (
                    <a key={i} href={fileUrl(a.path)} target="_blank" rel="noreferrer" data-testid={`history-attachment-${histAction.id}-${i}`}
                      className="inline-flex items-center gap-1 text-xs rounded-full bg-[#F0EAE1] text-[#8C532B] px-2 py-0.5 hover:underline">
                      <Paperclip className="h-3 w-3" /> {a.name}
                    </a>
                  ))}
                </div>
              )}
              {histAction.edited_at && (
                <p className="text-xs text-[#5F6B64] pt-1" data-testid="history-record-edited-info">
                  Modificato il {fmt(histAction.edited_at)}{histAction.edited_by_name ? ` da ${histAction.edited_by_name}` : ""}
                </p>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {perms.canOps && (
              <Button data-testid="edit-history-record-btn" onClick={() => openHistEdit(histAction)} className="bg-[#1B3B2B] hover:bg-[#142E1F]">
                <Pencil className="h-4 w-4 mr-2" /> Modifica record storico
              </Button>
            )}
            {perms.canManage && (
              <Button variant="outline" data-testid="delete-history-record-btn" onClick={() => { const r = histAction; setHistAction(null); setDelHist(r); }}
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
                <Trash2 className="h-4 w-4 mr-2" /> Elimina
              </Button>
            )}
            <Button variant="ghost" onClick={() => setHistAction(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form correzione record storico sanitario (solo dati documentali) */}
      <Dialog open={!!histEdit} onOpenChange={(o) => !o && setHistEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="history-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">Modifica record storico</DialogTitle></DialogHeader>
          {histEdit && (
            <div className="space-y-4">
              <div className="rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] px-3 py-2 text-xs text-[#5F6B64]">
                Stai correggendo una registrazione già completata di <span className="font-semibold text-[#1B3B2B]">{histEdit.horse_name}</span> ({histEdit.category}). È una correzione dello storico: la scadenza attiva e le ricorrenze future non verranno modificate.
              </div>
              <div><Label>Data completamento *</Label><DateField testId="hist-completion-date" className="mt-1.5" value={histEdit.completion_date} onChange={(v) => setHistEdit({ ...histEdit, completion_date: v })} /></div>
              <div><Label>Descrizione</Label><Input className="mt-1.5" data-testid="hist-description" value={histEdit.description} onChange={(e) => setHistEdit({ ...histEdit, description: e.target.value })} /></div>
              <div><Label>Note</Label><Textarea className="mt-1.5" data-testid="hist-notes" value={histEdit.notes} onChange={(e) => setHistEdit({ ...histEdit, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistEdit(null)}>Annulla</Button>
            <Button onClick={() => setHistEditConfirm(true)} disabled={!histEdit?.completion_date} data-testid="hist-save-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              Salva correzione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma forte modifica record storico sanitario */}
      <Dialog open={histEditConfirm} onOpenChange={(o) => !o && setHistEditConfirm(false)}>
        <DialogContent data-testid="hist-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display text-[#8C532B]">Confermi la modifica del record storico?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#2D3832]">
            Stai modificando un <span className="font-semibold">dato storico</span> già registrato. Questa è una <span className="font-semibold">correzione</span> dello storico sanitario, non una nuova esecuzione: la scadenza attiva e le ricorrenze future non verranno modificate.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistEditConfirm(false)}>Annulla</Button>
            <Button onClick={saveHistEdit} disabled={histSaving} data-testid="hist-confirm-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              {histSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Conferma modifica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete health-history confirm dialog */}
      <Dialog open={!!delHist} onOpenChange={(o) => !o && setDelHist(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-red-700">Elimina dallo storico sanitario</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#2D3832]">
              Stai eliminando definitivamente una registrazione dallo storico sanitario del cavallo.
              Questa operazione non elimina semplicemente una scadenza e non può essere annullata. Vuoi continuare?
            </p>
            {delHist && (
              <div className="rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] px-3 py-2 text-sm">
                <p className="font-semibold text-[#1B3B2B]">{delHist.category}</p>
                <p className="text-xs text-[#5F6B64]">{delHist.horse_name} · Completato {fmt(delHist.completion_date)}</p>
                {delHist.attachments?.length > 0 && (
                  <p className="text-xs text-[#8C532B] mt-1">Verranno eliminati anche {delHist.attachments.length} allegato/i.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelHist(null)}>Annulla</Button>
            <Button onClick={deleteHistory} data-testid="confirm-delete-history-btn" className="bg-red-600 hover:bg-red-700 text-white">
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Suspend monitoring confirm dialog */}
      <Dialog open={!!suspendItem} onOpenChange={(o) => !o && setSuspendItem(null)}>
        <DialogContent data-testid="suspend-monitoring-dialog">
          <DialogHeader><DialogTitle className="font-display">Sospendi monitoraggio</DialogTitle></DialogHeader>
          <p className="text-sm text-[#2D3832]">
            La scadenza resterà registrata con il suo stato e la sua data reali, ma non comparirà più tra le attività da gestire, nella Dashboard o nelle future notifiche. Potrai riattivarla in qualsiasi momento.
          </p>
          {suspendItem && <p className="text-sm text-[#5F6B64]">{suspendItem.horse_name} · {suspendItem.category}</p>}
          <div>
            <Label>Nota / motivo (facoltativo)</Label>
            <Textarea className="mt-1.5" data-testid="suspend-monitoring-note" value={suspendNote} onChange={(e) => setSuspendNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendItem(null)}>Annulla</Button>
            <Button onClick={doSuspend} disabled={saving} data-testid="suspend-monitoring-confirm" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sospendi monitoraggio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ricerca copertura prestazioni (population-first) */}
      <Dialog open={covOpen} onOpenChange={(o) => !o && setCovOpen(false)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]" data-testid="coverage-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-1.5">
              <Radar className="h-4 w-4 text-[#1B3B2B]" /> Ricerca copertura
              <InfoHint testId="help-coverage" label="Cosa fa la Ricerca copertura" text="Parte dalla popolazione cavalli e mostra chi ha o non ha una prestazione. «Nessun record» significa che non esiste alcun record per quella categoria; è diverso da «Da regolarizzare», che è un record esistente ma senza data valida." />
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Prestazione *</Label>
              <div className="mt-1.5"><SearchSelect testId="coverage-category" placeholder="Cerca prestazione…" items={activeCats.map((c) => ({ value: c.name, label: c.name }))} value={covCat} onChange={setCovCat} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cavalli</Label>
                <Select value={covPresence} onValueChange={setCovPresence}>
                  <SelectTrigger className="mt-1.5" data-testid="coverage-presence"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="presente" data-testid="coverage-presence-presente">Presenti</SelectItem>
                    <SelectItem value="all" data-testid="coverage-presence-all">Tutti</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Stato copertura</Label>
                <Select value={covStatus} onValueChange={setCovStatus}>
                  <SelectTrigger className="mt-1.5" data-testid="coverage-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COV_STATES.map((s) => <SelectItem key={s.v} value={s.v} data-testid={`coverage-status-${s.v}`}>{s.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={runCoverage} disabled={covLoading} data-testid="coverage-search-btn" className="w-full bg-[#1B3B2B] hover:bg-[#142E1F]">
              {covLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />} Cerca
            </Button>

            {covData && (
              <div className="space-y-2" data-testid="coverage-results">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-xs text-[#5F6B64] flex-1">{covData.total} cavall{covData.total === 1 ? "o" : "i"} nella popolazione · {covData.results.length} in elenco</p>
                  {perms.canOps && covData.results.length > 0 && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" data-testid="coverage-select-all" onClick={selectAllCov}>Seleziona tutti</Button>
                      <Button variant="ghost" size="sm" data-testid="coverage-deselect-all" onClick={deselectCov}>Deseleziona</Button>
                    </div>
                  )}
                </div>
                {covData.results.length === 0 ? (
                  <div className="rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] py-8 text-center text-sm text-[#5F6B64]" data-testid="coverage-empty">Nessun cavallo per questo filtro.</div>
                ) : (
                  <div className="rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden max-h-[46vh] overflow-y-auto">
                    {covData.results.map((r) => {
                      const meta = COV_META[r.state] || COV_META.no_record;
                      const selectable = perms.canOps;
                      const on = covSelected.includes(r.horse_id);
                      return (
                        <div key={r.horse_id} data-testid={`coverage-row-${r.horse_id}`} className={`flex items-center gap-3 px-3 py-2.5 ${on ? "bg-[#F7F3EC]" : "bg-white"}`}>
                          {selectable && (
                            <input type="checkbox" data-testid={`coverage-select-${r.horse_id}`} checked={on}
                              onChange={() => toggleCovSel(r.horse_id)} className="h-4 w-4 rounded border-[#C9BDA0] accent-[#1B3B2B] shrink-0" />
                          )}
                          <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ background: meta.dot }} />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-[#1B3B2B] truncate">{r.horse_name}</p>
                            <p className={`text-xs font-medium ${meta.cls}`}>{meta.l}{r.date ? ` · ${fmt(r.date)}` : ""}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          {perms.canOps && covSelected.length > 0 && (
            covAction ? (
              <div className="rounded-lg border border-[#1B3B2B] bg-[#F7F3EC] p-3 space-y-2" data-testid="coverage-action-confirm">
                <p className="text-sm font-semibold text-[#1B3B2B]">{covAction.managed_by === "proprietario" ? "Gestita dal proprietario" : "Gestita dalla scuderia / Da monitorare"} · {covAction.preview.total} cavall{covAction.preview.total === 1 ? "o" : "i"}</p>
                {covAction.managed_by === "proprietario" ? (
                  <p className="text-xs text-[#5F6B64]" data-testid="coverage-action-summary">Verranno impostati come gestiti dal proprietario. Nessuna scadenza o data viene creata; escono dalla copertura operativa della scuderia.</p>
                ) : (
                  <p className="text-xs text-[#5F6B64]" data-testid="coverage-action-summary">Da regolarizzare (nuovi): {covAction.preview.counts.to_regularize} · Con record esistente: {covAction.preview.counts.already_record}. Nessuna data inventata.</p>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" data-testid="coverage-action-back" onClick={() => setCovAction(null)}>Indietro</Button>
                  <Button size="sm" data-testid="coverage-action-confirm-btn" disabled={covActing} onClick={confirmRespAction} className="bg-[#1B3B2B] hover:bg-[#142E1F]">{covActing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Conferma</Button>
                </div>
              </div>
            ) : (
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <span className="text-sm font-semibold text-[#1B3B2B] mr-auto self-center" data-testid="coverage-selected-count">{covSelected.length} selezionat{covSelected.length === 1 ? "o" : "i"}</span>
                <Button variant="outline" size="sm" data-testid="coverage-act-scuderia" disabled={covActing} onClick={() => runRespAction("scuderia")}>Gestita dalla scuderia</Button>
                <Button variant="outline" size="sm" data-testid="coverage-act-proprietario" disabled={covActing} onClick={() => runRespAction("proprietario")}>Gestita dal proprietario</Button>
                <Button size="sm" onClick={createFromCoverage} data-testid="coverage-create-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
                  <Plus className="h-4 w-4 mr-1.5" /> Crea scadenze
                </Button>
              </DialogFooter>
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Self-contained create/edit dialog for a Scadenza. Single source used by the Scadenzario page AND
// by Horse Detail (quick-add). Owns its working state (seeded from `initial`) and all business logic
// (missing/regularize, auto due recompute, resolve-preview, multi-horse confirm, register). Fetches
// its own categories when `activeCats` is not supplied (Horse Detail case). No duplication.
export function DeadlineFormDialog({ stableId, horses, activeCats: propActiveCats, initial, onClose, onSaved }) {
  const [dialog, setDialog] = useState(() => ({ ...EMPTY, ...initial }));
  const [saving, setSaving] = useState(false);
  const [multiConfirm, setMultiConfirm] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fetchedCats, setFetchedCats] = useState(null);
  useEffect(() => {
    if (!propActiveCats) api.get(`/stables/${stableId}/deadline-categories`).then((r) => setFetchedCats(r.data)).catch(() => setFetchedCats([]));
  }, [propActiveCats, stableId]);
  const activeCats = propActiveCats || (fetchedCats || []).filter((c) => !c.archived);

  const buildBase = () => ({ category: dialog.category, description: dialog.description || null,
    due_date: dialog.is_missing ? null : (dialog.due_date || null),
    last_completion_date: dialog.last_completion_date || null, recurrence: dialog.recurrence,
    reminder_days: dialog.reminder_days ? Number(dialog.reminder_days) : null,
    managed_by: dialog.managed_by || "scuderia", is_missing: !!dialog.is_missing, notes: dialog.notes || null });
  const save = (e) => {
    if (e) e.preventDefault();
    if (dialog.id) { if (!dialog.horse_id) return toast.error("Seleziona un cavallo"); }
    else if (!(dialog.horse_ids || []).length) return toast.error("Seleziona almeno un cavallo");
    if (!(dialog.category || "").trim()) return toast.error("Seleziona il tipo di scadenza");
    if (!dialog.id && !dialog.is_missing && dialog.last_completion_date) { openPreview(); return; }
    if (!dialog.is_missing && !dialog.due_date) return toast.error("Indica una data di scadenza oppure segna la scadenza come «Mancante»");
    if (!dialog.id && dialog.horse_ids.length > 1) { setMultiConfirm(true); return; }
    doSave();
  };
  const recomputeDue = (d) => {
    if (!d.due_auto || d.is_missing) return d;
    if (d.last_completion_date && RMONTHS[d.recurrence]) return { ...d, due_date: addMonthsISO(d.last_completion_date, RMONTHS[d.recurrence]) };
    return d;
  };
  const setLastComp = (v) => setDialog((d) => recomputeDue({ ...d, last_completion_date: v }));
  const setRecurrence = (v) => setDialog((d) => recomputeDue({ ...d, recurrence: v }));
  const setDue = (v) => setDialog((d) => ({ ...d, due_date: v, due_auto: false }));
  const resetAuto = () => setDialog((d) => recomputeDue({ ...d, due_auto: true }));
  const buildReg = () => ({
    horse_ids: dialog.horse_ids, category: dialog.category, completion_date: dialog.last_completion_date,
    recurrence: dialog.recurrence, next_due_date: dialog.recurrence === "custom" ? (dialog.due_date || null) : null,
    managed_by: dialog.managed_by || "scuderia", reminder_days: dialog.reminder_days ? Number(dialog.reminder_days) : null,
    description: dialog.description || null, notes: dialog.notes || null,
  });
  const openPreview = async () => {
    setPreview({ loading: true });
    try {
      const r = await api.post(`/stables/${stableId}/deadlines/resolve-preview`, buildReg());
      const token = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
      setPreview({ loading: false, data: r.data, token });
    } catch (err) { toast.error(errText(err.response?.data?.detail)); setPreview(null); }
  };
  const doRegister = async () => {
    if (!preview?.data || preview.data.has_conflict) return;
    setSaving(true);
    try {
      const r = await api.post(`/stables/${stableId}/deadlines/register`, { ...buildReg(), token: preview.token });
      toast.success(`Registrate ${r.data.applied} scadenze`);
      setPreview(null); onSaved && onSaved(); onClose();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const doSave = async () => {
    setSaving(true);
    const base = buildBase();
    try {
      if (dialog.id) await api.put(`/stables/${stableId}/deadlines/${dialog.id}`, { ...base, horse_id: dialog.horse_id });
      else await api.post(`/stables/${stableId}/deadlines`, { ...base, horse_id: dialog.horse_ids[0] });
      toast.success("Scadenza salvata");
      onSaved && onSaved(); onClose();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const doBatch = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/stables/${stableId}/deadlines/batch`, { ...buildBase(), horse_ids: dialog.horse_ids, skip_existing: !!dialog._coverage });
      const skipped = r.data.skipped || [];
      if (r.data.created > 0) toast.success(`Create ${r.data.created} scadenze`);
      if (skipped.length) toast.warning(`${skipped.length} cavall${skipped.length === 1 ? "o" : "i"} già con un record: ${skipped.map((s) => s.horse_name).join(", ")}`);
      if (r.data.created === 0 && !skipped.length) toast.info("Nessuna scadenza creata");
      setMultiConfirm(false); onSaved && onSaved(); onClose();
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{dialog?.id ? "Modifica scadenza" : "Nuova scadenza"}</DialogTitle></DialogHeader>
          {dialog && (
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label>{dialog.id ? "Cavallo *" : "Cavalli *"}</Label>
                {dialog.id ? (
                  <div className="mt-1.5"><HorsePicker testId="deadline-horse" horses={horses} value={dialog.horse_id} onChange={(id) => setDialog({ ...dialog, horse_id: id })} /></div>
                ) : (
                  <div className="mt-1.5"><HorsePicker multiple testId="deadline-horse" horses={horses} value={dialog.horse_ids} onChange={(ids) => setDialog({ ...dialog, horse_ids: ids })} /></div>
                )}
              </div>
              <div>
                <Label>Categoria *</Label>
                <div className="mt-1.5"><SearchSelect testId="deadline-category" placeholder="Cerca tipo scadenza…" items={activeCats.map((c) => ({ value: c.name, label: c.name }))} value={dialog.category} onChange={(v) => setDialog({ ...dialog, category: v })} /></div>
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="deadline-missing-toggle" checked={!!dialog.is_missing}
                    onChange={(e) => setDialog({ ...dialog, is_missing: e.target.checked })}
                    className="h-4 w-4 rounded border-[#C9BDA0] accent-[#1B3B2B]" />
                  <span className="text-sm text-[#1B3B2B]">Mancante / da regolarizzare</span>
                </label>
                <p className="text-xs text-[#8C6D4F] mt-1 ml-6">Usa questa opzione quando l'adempimento o il documento non è attualmente presente.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ultimo completamento</Label><DateField testId="deadline-lastcomp" className="mt-1.5" value={dialog.last_completion_date || ""} onChange={setLastComp} /></div>
                {!dialog.is_missing && (
                  <div>
                    <div className="flex items-center justify-between">
                      <Label>{dialog.last_completion_date ? "Prossima scadenza" : "Data scadenza *"}</Label>
                      {!dialog.due_auto && dialog.last_completion_date && RMONTHS[dialog.recurrence] && (
                        <button type="button" data-testid="deadline-due-auto" onClick={resetAuto} className="text-[11px] text-[#8C532B] hover:underline">Auto</button>
                      )}
                    </div>
                    <DateField testId="deadline-due" className="mt-1.5" value={dialog.due_date || ""} onChange={setDue} />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ricorrenza</Label>
                  <Select value={dialog.recurrence} onValueChange={setRecurrence}>
                    <SelectTrigger className="mt-1.5" data-testid="deadline-recurrence"><SelectValue /></SelectTrigger>
                    <SelectContent>{RECURRENCE.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Avvisami (giorni prima)</Label><Input type="number" className="mt-1.5" value={dialog.reminder_days ?? ""} onChange={(e) => setDialog({ ...dialog, reminder_days: e.target.value })} /></div>
              </div>
              <div>
                <Label>Gestito da</Label>
                <Select value={dialog.managed_by || "scuderia"} onValueChange={(v) => setDialog({ ...dialog, managed_by: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="deadline-managed-by"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scuderia">Scuderia</SelectItem>
                    <SelectItem value="proprietario">Proprietario</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Descrizione</Label><Input className="mt-1.5" value={dialog.description || ""} onChange={(e) => setDialog({ ...dialog, description: e.target.value })} /></div>
              <div><Label>Note</Label><Textarea className="mt-1.5" value={dialog.notes || ""} onChange={(e) => setDialog({ ...dialog, notes: e.target.value })} /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onClose()}>Annulla</Button>
                <Button type="submit" disabled={saving} data-testid="save-deadline-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={multiConfirm} onOpenChange={(o) => !o && setMultiConfirm(false)}>
        <DialogContent data-testid="deadline-multi-confirm">
          <DialogHeader><DialogTitle className="font-display">Conferma creazione multipla</DialogTitle></DialogHeader>
          <p className="text-sm text-[#2D3832]">
            {dialog?.is_missing
              ? <>Stai per creare <span className="font-semibold">{dialog?.category}</span> come <span className="font-semibold text-[#A03D62]">Mancante</span> per <span className="font-semibold">{dialog?.horse_ids?.length}</span> cavalli.</>
              : <>Stai per creare <span className="font-semibold">{dialog?.category}</span> per <span className="font-semibold">{dialog?.horse_ids?.length}</span> cavalli.</>}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMultiConfirm(false)}>Annulla</Button>
            <Button onClick={doBatch} disabled={saving} data-testid="deadline-multi-confirm-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Crea {dialog?.horse_ids?.length} scadenze
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="deadline-preview" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-1.5">{dialog?.category || "Registrazione"}<InfoHint testId="help-regolarizza" label="Cosa fa la registrazione (Regolarizza)" text="Usa Regolarizza quando la prestazione risulta mancante o senza una scadenza valida e vuoi registrare la situazione reale." /></DialogTitle></DialogHeader>
          {preview?.loading ? (
            <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#8C6D4F]" /></div>
          ) : preview?.data ? (
            <div className="space-y-3">
              <p className="text-sm text-[#5F6B64]">Eseguito il <span className="font-semibold text-[#1B3B2B]">{fmt(dialog?.last_completion_date)}</span> · {preview.data.plans.length} cavall{preview.data.plans.length === 1 ? "o" : "i"}</p>
              <div className="text-sm space-y-0.5 rounded-lg bg-[#F7F4EE] p-3" data-testid="deadline-preview-summary">
                {preview.data.summary.complete_existing > 0 && <p>{preview.data.summary.complete_existing} scadenze esistenti verranno completate</p>}
                {preview.data.summary.regularize_missing > 0 && <p>{preview.data.summary.regularize_missing} record Mancante verranno regolarizzati</p>}
                {preview.data.summary.supersede_future > 0 && <p className="text-[#9A6516]">{preview.data.summary.supersede_future} scadenze ancora valide verranno anticipate</p>}
                {preview.data.summary.create_new > 0 && <p>{preview.data.summary.create_new} nuovi record verranno creati</p>}
                {preview.data.summary.conflict > 0 && <p className="text-[#B4232A] font-medium">{preview.data.summary.conflict} situazioni ambigue da verificare</p>}
              </div>
              <div className="space-y-1.5">
                {preview.data.plans.map((p) => {
                  const meta = STATE_META[p.state] || STATE_META.CREATE_NEW;
                  return (
                    <div key={p.horse_id} data-testid={`preview-row-${p.horse_id}`} className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${meta.cls}`}>
                      <span className="mt-0.5 shrink-0">
                        {meta.icon === "warn" ? <RefreshCw className="h-4 w-4 text-[#9A6516]" /> : meta.icon === "alert" ? <AlertTriangle className="h-4 w-4 text-[#B4232A]" /> : meta.icon === "plus" ? <Plus className="h-4 w-4 text-[#1B3B2B]" /> : <CheckCircle2 className="h-4 w-4 text-[#1F7A45]" />}
                      </span>
                      <div className="min-w-0"><p className="font-semibold text-[#1B3B2B] text-sm">{p.horse_name}</p><p className="text-xs text-[#5F6B64]">{p.message}</p></div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPreview(null)}>Annulla</Button>
            <Button onClick={doRegister} disabled={saving || preview?.loading || !preview?.data || preview?.data?.has_conflict} data-testid="deadline-preview-confirm" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {preview?.data?.has_conflict ? "Verifica i conflitti" : `Conferma per ${preview?.data?.plans?.length || 0} cavalli`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
